import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";

interface SaveSection {
  id: string;
  label: string;
  isDirty: boolean;
  onSave: () => void;
  loading: boolean;
}

interface SaveSectionsContextValue {
  sections: Map<string, SaveSection>;
  registerSection: (section: SaveSection) => void;
  unregisterSection: (id: string) => void;
}

const SaveSectionsContext = createContext<SaveSectionsContextValue>({
  sections: new Map(),
  registerSection: () => {},
  unregisterSection: () => {},
});

export const SaveSectionsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [sections, setSections] = useState<Map<string, SaveSection>>(
    new Map(),
  );

  const registerSection = useCallback((section: SaveSection) => {
    setSections((prev) => new Map(prev).set(section.id, section));
  }, []);

  const unregisterSection = useCallback((id: string) => {
    setSections((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  return (
    <SaveSectionsContext.Provider
      value={{ sections, registerSection, unregisterSection }}
    >
      {children}
    </SaveSectionsContext.Provider>
  );
};

export function useRegisterSaveSection({
  id,
  label,
  isDirty,
  onSave,
  loading,
}: {
  id: string;
  label: string;
  isDirty: boolean;
  onSave: () => void;
  loading: boolean;
}) {
  const { registerSection, unregisterSection } = useContext(SaveSectionsContext);

  // Always keep a ref to the latest onSave to avoid stale closures
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const stableSave = useCallback(() => onSaveRef.current(), []);

  useEffect(() => {
    registerSection({ id, label, isDirty, loading, onSave: stableSave });
  }, [id, label, isDirty, loading, stableSave, registerSection]);

  useEffect(() => {
    return () => unregisterSection(id);
  }, [id, unregisterSection]);
}

export function useSaveSections() {
  const { sections } = useContext(SaveSectionsContext);
  return Array.from(sections.values());
}
